// Credit / Due (Udhar) — main hub UI. Edit this file; `CreditDueHub.jsx` re-exports for the existing route import.
import React, { useDeferredValue, useEffect, useMemo, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Toaster } from "react-hot-toast";
import {
  FileDown,
  FilePlus2,
  History,
  ListFilter,
  RefreshCw,
  Search,
  Table2,
  Wallet,
} from "lucide-react";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import MenuBar from "../../components/MenuBar";
import HeaderBar from "../../components/HeaderBar";
import { useAuth } from "../../context/AuthContext";
import {
  daysOutstandingForBill,
  fetchAllCreditBills,
  effectiveDueDateForOverdue,
  fetchAuditNotesForCustomer,
  summarizeCustomersFromBills,
  creditBillBranch,
} from "../../services/creditDueService";
import { useCreditBranchFilter } from "./useCreditBranchFilter";
import CreditBranchSelector from "./CreditBranchSelector";
import CreditReceivePaymentContent from "./CreditReceivePaymentContent";
import CreditAddCreditContent from "./CreditAddCreditContent";

function round2(n) {
  return Math.round((Number(n) || 0) * 100) / 100;
}

function ageBucket(days) {
  const d = Number(days) || 0;
  if (d <= 30) return "b0_30";
  if (d <= 60) return "b31_60";
  if (d <= 90) return "b61_90";
  return "b90_plus";
}

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

export default function CreditDuePage() {
  const { user: storedUser } = useAuth();
  const { branch, setBranch, visibleBranches } = useCreditBranchFilter(storedUser);

  const [searchParams, setSearchParams] = useSearchParams();
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [alertFilter, setAlertFilter] = useState("all");
  const deferredSearch = useDeferredValue(search);
  const [showCreditDays, setShowCreditDays] = useState(() => {
    try {
      return localStorage.getItem("creditDue_showCreditDays") === "1";
    } catch {
      return false;
    }
  });

  const [receiveOpen, setReceiveOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [receivePrefill, setReceivePrefill] = useState("");
  const [receiveKey, setReceiveKey] = useState(0);
  const [addKey, setAddKey] = useState(0);
  const [auditMetaByCustomer, setAuditMetaByCustomer] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const rows = await fetchAllCreditBills();
      setBills(rows.map((b) => ({ ...b, _daysOutstanding: daysOutstandingForBill(b) })));
    } catch (e) {
      setErr(e?.message || "Failed to load credit bills.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (searchParams.get("receive") !== "1") return;
    const cid = searchParams.get("customerId") || "";
    setReceivePrefill(cid);
    setReceiveKey((k) => k + 1);
    setReceiveOpen(true);
    setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams]);

  const openReceive = (customerId = "") => {
    setReceivePrefill(customerId || "");
    setReceiveKey((k) => k + 1);
    setReceiveOpen(true);
  };

  const openAdd = () => {
    setAddKey((k) => k + 1);
    setAddOpen(true);
  };

  const billsBranch = useMemo(
    () => bills.filter((b) => creditBillBranch(b) === branch),
    [bills, branch]
  );

  const billsOtherBranchCount = useMemo(
    () => bills.filter((b) => creditBillBranch(b) !== branch).length,
    [bills, branch]
  );

  const customersWithOpenDue = useMemo(() => summarizeCustomersFromBills(billsBranch), [billsBranch]);

  /** True when this branch still has at least one bill with balance > 0 (used for empty-state copy). */
  const branchHasOpenBalance = useMemo(
    () => billsBranch.some((b) => round2(b.balanceAmount) > 0),
    [billsBranch]
  );

  const rows = useMemo(() => {
    const q = deferredSearch.trim().toLowerCase();
    let list = [...customersWithOpenDue];
    if (q) {
      list = list.filter(
        (c) =>
          (c.customerName || "").toLowerCase().includes(q) ||
          (c.phone || "").includes(q) ||
          (c.customerId || "").toLowerCase().includes(q)
      );
    }
    if (filter === "overdue") list = list.filter((c) => c.hasOverdue);

    const out = list.map((c) => {
      const openBills = billsBranch.filter(
        (b) => b.customerId === c.customerId && round2(b.balanceAmount) > 0
      );
      const buckets = { b0_30: 0, b31_60: 0, b61_90: 0, b90_plus: 0 };
      let dueToday = 0;
      let dueOver15 = 0;
      for (const b of openBills) {
        const bal = round2(b.balanceAmount);
        const days = Math.max(0, Number(b._daysOutstanding || 0));
        buckets[ageBucket(days)] += bal;
        if (days > 15) dueOver15 += bal;
        const due = effectiveDueDateForOverdue(b);
        if (due && due.toISOString().slice(0, 10) === todayIso()) dueToday += bal;
      }
      const overdueAmt = Number(c.overdueAmount) || 0;
      const overdueDays = Math.max(0, Number(c.totalCreditDays) || 0);
      const priorityScore = Math.round(overdueAmt / 1000 + overdueDays / 10);
      return {
        ...c,
        ...buckets,
        dueToday,
        dueOver15: round2(dueOver15),
        priorityScore,
        highValueOverdue: overdueAmt >= 50000,
      };
    });

    let filteredOut = out;
    if (alertFilter === "dueToday") filteredOut = out.filter((r) => Number(r.dueToday) > 0);
    if (alertFilter === "highValue") filteredOut = out.filter((r) => r.highValueOverdue);

    return filteredOut.sort((a, b) => {
      if (b.priorityScore !== a.priorityScore) return b.priorityScore - a.priorityScore;
      return b.totalDue - a.totalDue;
    });
  }, [customersWithOpenDue, deferredSearch, filter, billsBranch, alertFilter]);

  const dueListTotals = useMemo(() => {
    let totalDue = 0;
    let totalCreditDays = 0;
    let overdueSum = 0;
    for (const r of rows) {
      totalDue += Number(r.totalDue) || 0;
      totalCreditDays += typeof r.totalCreditDays === "number" ? r.totalCreditDays : 0;
      overdueSum += Number(r.overdueAmount) || 0;
    }
    return { totalDue, totalCreditDays, overdueSum, count: rows.length };
  }, [rows]);

  const paidRows = useMemo(() => {
    const q = deferredSearch.trim().toLowerCase();
    const paidBills = billsBranch.filter((b) => round2(b.balanceAmount) <= 0);
    const map = new Map();
    for (const b of paidBills) {
      const cid = b.customerId;
      if (!cid) continue;
      if (!map.has(cid)) {
        map.set(cid, {
          customerId: cid,
          customerName: b.customerName || "",
          phone: b.phone || "",
          cleared: 0,
        });
      }
      map.get(cid).cleared += 1;
    }
    let list = Array.from(map.values());
    if (q) {
      list = list.filter(
        (c) =>
          (c.customerName || "").toLowerCase().includes(q) ||
          (c.phone || "").includes(q)
      );
    }
    return list;
  }, [billsBranch, deferredSearch]);

  const paidRowsTotals = useMemo(
    () => ({
      bills: paidRows.reduce((s, r) => s + (Number(r.cleared) || 0), 0),
      count: paidRows.length,
    }),
    [paidRows]
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const ids = rows.map((r) => r.customerId).filter(Boolean).slice(0, 80);
        const pairs = await Promise.all(
          ids.map(async (id) => {
            const notes = await fetchAuditNotesForCustomer(id);
            const latest = notes[0] || null;
            return [id, latest];
          })
        );
        if (!cancelled) setAuditMetaByCustomer(Object.fromEntries(pairs));
      } catch {
        if (!cancelled) setAuditMetaByCustomer({});
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [rows]);

  const alerts = useMemo(() => {
    const dueTodayCount = rows.filter((r) => Number(r.dueToday) > 0).length;
    const due15Count = rows.filter((r) => Number(r.dueOver15) > 0).length;
    const highValueOverdueCount = rows.filter((r) => r.highValueOverdue).length;
    return {
      dueTodayCount,
      dueTodayAmount: rows.reduce((s, r) => s + (Number(r.dueToday) || 0), 0),
      due15Count,
      due15Amount: rows.reduce((s, r) => s + (Number(r.dueOver15) || 0), 0),
      highValueOverdueCount,
      highValueOverdueAmount: rows
        .filter((r) => r.highValueOverdue)
        .reduce((s, r) => s + (Number(r.overdueAmount) || 0), 0),
    };
  }, [rows]);

  const exportRows = useMemo(
    () =>
      rows.map((r) => ({
        Customer: r.customerName || "",
        Phone: r.phone || "",
        "Total Due": round2(r.totalDue),
        ...(showCreditDays ? { "Credit Days Σ": Number(r.totalCreditDays) || 0 } : {}),
        Overdue: round2(r.overdueAmount),
        "0-30": round2(r.b0_30),
        "31-60": round2(r.b31_60),
        "61-90": round2(r.b61_90),
        "90+": round2(r.b90_plus),
        "Priority Score": r.priorityScore || 0,
      })),
    [rows, showCreditDays]
  );

  const exportXlsx = (kind) => {
    const data =
      kind === "overdue"
        ? exportRows.filter((r) => Number(r.Overdue) > 0)
        : kind === "aging"
          ? exportRows.map((r) => ({
              Customer: r.Customer,
              "0-30": r["0-30"],
              "10-60": r["10-60"],
              "61-90": r["61-90"],
              "90+": r["90+"],
            }))
          : exportRows;
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(data);
    XLSX.utils.book_append_sheet(wb, ws, "CreditDue");
    const br = String(branch || "Main").replace(/\s+/g, "-");
    XLSX.writeFile(wb, `credit-due-${kind}-${br}-${todayIso()}.xlsx`);
  };

  const exportPdf = (kind) => {
    const data =
      kind === "overdue"
        ? exportRows.filter((r) => Number(r.Overdue) > 0)
        : kind === "aging"
          ? exportRows.map((r) => ({
              Customer: r.Customer,
              "0-30": r["0-30"],
              "31-60": r["31-60"],
              "61-90": r["61-90"],
              "90+": r["90+"],
            }))
          : exportRows;
    const doc = new jsPDF({ orientation: "landscape" });
    const headers = Object.keys(data[0] || { Customer: "", "Total Due": "" });
    autoTable(doc, {
      head: [headers],
      body: data.map((r) => headers.map((h) => r[h] ?? "")),
      styles: { fontSize: 8 },
      headStyles: { fillColor: [15, 23, 42] },
      margin: { top: 16 },
    });
    const br = String(branch || "Main").replace(/\s+/g, "-");
    doc.save(`credit-due-${kind}-${br}-${todayIso()}.pdf`);
  };

  return (
    <div className="min-h-screen bg-slate-100/90">
      <Toaster position="top-right" />
      <MenuBar />
      <HeaderBar />
      <main className="w-full px-2 pt-3 sm:px-3 lg:h-[calc(100vh-84px)] lg:min-h-0 lg:overflow-hidden">
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-[1fr_22rem] lg:items-start lg:h-full lg:min-h-0">
          {/* LEFT: summary + table */}
          <section className="order-2 lg:order-1 lg:flex lg:flex-col lg:h-full lg:min-h-0">
            <div className="mb-3 grid grid-cols-1 gap-2 sm:grid-cols-3">
              <button
                type="button"
                onClick={() => setAlertFilter((prev) => (prev === "dueToday" ? "all" : "dueToday"))}
                className={`rounded-lg border p-3 text-left ${
                  alertFilter === "dueToday"
                    ? "border-indigo-300 bg-indigo-50"
                    : "border-slate-200 bg-white hover:bg-slate-50"
                }`}
                title="Show due-today customer details below"
              >
                <p className="text-[11px] font-bold uppercase text-slate-500">Due today</p>
                <p className="mt-1 text-lg font-bold text-slate-900">
                  ₹{round2(alerts.dueTodayAmount).toLocaleString("en-IN")}
                </p>
                <p className="text-xs text-slate-600">{alerts.dueTodayCount} customers</p>
              </button>
              <Link
                to="/credit-due/due-over-15"
                className="block rounded-lg border border-amber-200 bg-amber-50 p-3 hover:bg-amber-50/70 focus:outline-none focus:ring-2 focus:ring-amber-400"
                title="Open Due > 15 days details"
              >
                <p className="text-[11px] font-bold uppercase text-amber-700">Due &gt; 15 days</p>
                <p className="mt-1 text-lg font-bold text-amber-900">
                  ₹{round2(alerts.due15Amount).toLocaleString("en-IN")}
                </p>
                <p className="text-xs text-amber-700">{alerts.due15Count} customers</p>
              </Link>
              <div className="rounded-lg border border-rose-200 bg-rose-50 p-3">
                <p className="text-[11px] font-bold uppercase text-rose-700">High-value overdue</p>
                <p className="mt-1 text-lg font-bold text-rose-900">
                  ₹{round2(alerts.highValueOverdueAmount).toLocaleString("en-IN")}
                </p>
                <p className="text-xs text-rose-700">{alerts.highValueOverdueCount} customers</p>
              </div>
            </div>

            {err && (
              <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" role="alert">
                {err}
              </div>
            )}

            {alertFilter !== "all" && (
              <div className="mb-2 flex items-center justify-between rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2 text-xs text-indigo-900">
                <span>
                  Showing details for: <strong>{alertFilter === "dueToday" ? "Due today" : "High-value overdue"}</strong>
                </span>
                <button
                  type="button"
                  onClick={() => setAlertFilter("all")}
                  className="font-semibold underline"
                >
                  Clear
                </button>
              </div>
            )}

            <div className="lg:flex-1 lg:min-h-0">
              {loading ? (
                <p className="text-sm text-slate-600">Loading…</p>
              ) : filter === "paid" ? (
                paidRows.length === 0 ? (
                  <p className="text-sm text-slate-600">No cleared bills yet.</p>
                ) : (
                  <div className="lg:h-full lg:min-h-0 lg:overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                    <div className="lg:h-full lg:min-h-0 lg:overflow-y-auto">
                      <table className="min-w-full text-sm">
                    <thead className="bg-slate-50/95 backdrop-blur text-left text-xs font-bold uppercase text-slate-600 sticky top-0 z-10">
                          <tr>
                            <th className="p-3">Customer</th>
                            <th className="p-3">Phone</th>
                            <th className="p-3 text-right">Paid bills</th>
                          </tr>
                        </thead>
                        <tbody>
                      {paidRows.map((r) => (
                        <tr key={r.customerId} className="border-t border-slate-100 transition-colors hover:bg-slate-50/70">
                              <td className="p-3 font-semibold text-slate-900">
                                <Link to={`/credit-due/customer/${r.customerId}`} className="text-indigo-700 hover:underline">
                                  {r.customerName || "—"}
                                </Link>
                              </td>
                              <td className="p-3 text-slate-600">{r.phone || "—"}</td>
                              <td className="p-3 text-right tabular-nums">{r.cleared}</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot className="border-t-2 border-slate-300 bg-slate-100">
                          <tr>
                            <td className="p-3 text-xs font-bold uppercase text-slate-600" colSpan={2}>
                              Total ({paidRowsTotals.count} customers)
                            </td>
                            <td className="p-3 text-right text-sm font-bold tabular-nums text-slate-900">
                              {paidRowsTotals.bills.toLocaleString("en-IN")} bills
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>
                )
              ) : filter === "overdue" && rows.length === 0 ? (
                <p className="text-sm text-slate-600">No overdue balances in this view.</p>
              ) : rows.length === 0 ? (
                <div className="space-y-2 text-sm text-slate-600">
                  {deferredSearch.trim() ? (
                    <p>
                      No customer matches{" "}
                      <span className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-xs text-slate-800">
                        {deferredSearch.trim()}
                      </span>
                      . Clear the search to see the full list.
                    </p>
                  ) : bills.length === 0 ? (
                    <p>No credit bills in the database yet. Save a CREDIT invoice or add manual Udhar, then refresh.</p>
                  ) : billsBranch.length === 0 ? (
                    billsOtherBranchCount > 0 ? (
                      <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                        <strong className="font-semibold">{billsOtherBranchCount}</strong> bill
                        {billsOtherBranchCount === 1 ? "" : "s"} are on another outlet (missing{" "}
                        <strong className="font-semibold">branch</strong> counts as{" "}
                        <strong className="font-semibold">Main</strong>). Switch the branch above or set{" "}
                        <strong className="font-semibold">branch</strong> in{" "}
                        <Link className="font-semibold text-indigo-700 underline" to="/credit-due-master">
                          Credit / Due — data maintenance
                        </Link>
                        .
                      </p>
                    ) : (
                      <p>
                        No bills for{" "}
                        <strong className="text-slate-800">
                          {visibleBranches.find((x) => x.value === branch)?.label || branch}
                        </strong>
                        .
                      </p>
                    )
                  ) : !branchHasOpenBalance ? (
                    <p className="text-sm text-slate-600">
                      No open balances on this branch. Use{" "}
                      <strong className="text-slate-800">Cleared bills (history)</strong> in the filter to see customers with
                      fully paid bills.
                    </p>
                  ) : (
                    <p className="text-xs leading-relaxed text-slate-600">
                      Bills are on this branch but no customer rows were built. Open{" "}
                      <Link className="font-semibold text-indigo-700 underline" to="/credit-due-master">
                        data maintenance
                      </Link>{" "}
                      and confirm each bill has a <strong className="text-slate-800">customerId</strong> (Firestore field
                      name). Spreadsheet imports often use <strong className="text-slate-800">customerid</strong> — the app
                      maps that when loading the list; saving from the app writes canonical camelCase.
                    </p>
                  )}
                </div>
              ) : (
                <div className="lg:h-full lg:min-h-0 lg:overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                  <div className="lg:h-full lg:min-h-0 lg:overflow-auto">
                    <table className="min-w-full text-sm">
                      <thead className="bg-slate-50/95 backdrop-blur text-left text-xs font-bold uppercase text-slate-600 sticky top-0 z-10">
                        <tr>
                          <th className="p-3">Customer</th>
                          <th className="p-3">Phone</th>
                          <th className="p-3 text-right">Total due</th>
                          <th className="p-3 text-right">0-30</th>
                          <th className="p-3 text-right">31-60</th>
                          <th className="p-3 text-right">61-90</th>
                          <th className="p-3 text-right">90+</th>
                          {showCreditDays ? (
                            <th className="p-3 text-right" title="Sum of days since invoice (or bill date) on each open bill">
                              Credit days (Σ)
                            </th>
                          ) : null}
                          <th className="p-3 text-right">Overdue</th>
                          <th className="p-3 text-right">Priority</th>
                          <th className="p-3">Last contact</th>
                          <th className="p-3">Next action</th>
                          <th className="p-3" />
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((r, idx) => (
                          <tr
                            key={r.customerId}
                            className={`border-t border-slate-100 transition-colors hover:bg-slate-50/70 ${
                              r.hasOverdue ? "bg-amber-50/50" : idx % 2 ? "bg-white" : "bg-slate-50/20"
                            }`}
                          >
                            <td className="p-3 font-semibold text-slate-900">
                              <Link to={`/credit-due/customer/${r.customerId}`} className="text-indigo-700 hover:underline">
                                {r.customerName || "—"}
                              </Link>
                            </td>
                            <td className="p-3 text-slate-600">{r.phone || "—"}</td>
                            <td className="p-3 text-right font-bold tabular-nums text-slate-900">
                              ₹{round2(r.totalDue).toLocaleString("en-IN")}
                            </td>
                            <td className="p-3 text-right tabular-nums">₹{round2(r.b0_30).toLocaleString("en-IN")}</td>
                            <td className="p-3 text-right tabular-nums">₹{round2(r.b31_60).toLocaleString("en-IN")}</td>
                            <td className="p-3 text-right tabular-nums">₹{round2(r.b61_90).toLocaleString("en-IN")}</td>
                            <td className="p-3 text-right tabular-nums">₹{round2(r.b90_plus).toLocaleString("en-IN")}</td>
                            {showCreditDays ? (
                              <td
                                className="p-3 text-right tabular-nums text-slate-800"
                                title="Sum of days open on each unpaid bill"
                              >
                                {typeof r.totalCreditDays === "number" ? r.totalCreditDays.toLocaleString("en-IN") : "—"}
                              </td>
                            ) : null}
                            <td className="p-3 text-right tabular-nums text-amber-900">
                              {r.hasOverdue ? `₹${round2(r.overdueAmount).toLocaleString("en-IN")}` : "—"}
                            </td>
                            <td className="p-3 text-right tabular-nums font-semibold text-rose-700">{r.priorityScore}</td>
                            <td className="p-3 text-xs text-slate-700">
                              {auditMetaByCustomer[r.customerId]?.lastContactDate || "—"}
                            </td>
                            <td className="p-3 text-xs text-slate-700">
                              {auditMetaByCustomer[r.customerId]?.nextActionDate || "—"}
                            </td>
                            <td className="p-3 text-right">
                              <button
                                type="button"
                                onClick={() => openReceive(r.customerId)}
                                className="text-xs font-semibold text-indigo-600 hover:underline"
                              >
                                Pay
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="border-t-2 border-slate-300 bg-slate-100">
                        <tr>
                          <td className="p-3 text-xs font-bold uppercase text-slate-600" colSpan={2}>
                            Total ({dueListTotals.count} customers)
                          </td>
                          <td className="p-3 text-right text-sm font-bold tabular-nums text-slate-900">
                            ₹{round2(dueListTotals.totalDue).toLocaleString("en-IN")}
                          </td>
                          <td className="p-3 text-right text-sm font-bold tabular-nums text-slate-800">
                            ₹{round2(rows.reduce((s, r) => s + (Number(r.b0_30) || 0), 0)).toLocaleString("en-IN")}
                          </td>
                          <td className="p-3 text-right text-sm font-bold tabular-nums text-slate-800">
                            ₹{round2(rows.reduce((s, r) => s + (Number(r.b31_60) || 0), 0)).toLocaleString("en-IN")}
                          </td>
                          <td className="p-3 text-right text-sm font-bold tabular-nums text-slate-800">
                            ₹{round2(rows.reduce((s, r) => s + (Number(r.b61_90) || 0), 0)).toLocaleString("en-IN")}
                          </td>
                          <td className="p-3 text-right text-sm font-bold tabular-nums text-slate-800">
                            ₹{round2(rows.reduce((s, r) => s + (Number(r.b90_plus) || 0), 0)).toLocaleString("en-IN")}
                          </td>
                          {showCreditDays ? (
                            <td className="p-3 text-right text-sm font-bold tabular-nums text-slate-800">
                              {dueListTotals.totalCreditDays.toLocaleString("en-IN")}
                            </td>
                          ) : null}
                          <td className="p-3 text-right text-sm font-bold tabular-nums text-amber-900">
                            ₹{round2(dueListTotals.overdueSum).toLocaleString("en-IN")}
                          </td>
                          <td className="p-3 text-right text-sm font-bold tabular-nums text-rose-700">
                            {rows.reduce((s, r) => s + (Number(r.priorityScore) || 0), 0)}
                          </td>
                          <td className="p-3" />
                          <td className="p-3" />
                          <td className="p-3" />
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </section>

          {/* RIGHT: options — same visual language as main grid (slate / indigo / white cards) */}
          <aside className="order-1 flex flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm lg:order-2 lg:h-full lg:max-h-full lg:min-h-0">
            <div className="shrink-0 border-b border-slate-200 px-4 py-3 text-center">
              <h2 className="text-base font-extrabold text-slate-900">Credit / Due (Udhar)</h2>
              <p className="mt-1 text-[11px] leading-snug text-slate-600">
                Bill-wise balances from credit sales and manual entries.
              </p>
            </div>

            <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-4">
              <div className="rounded-lg border border-slate-200 bg-slate-50/80 p-3">
                <p className="text-[11px] font-bold uppercase text-slate-500">Branch</p>
                <CreditBranchSelector
                  id="credit-due-hub-branch"
                  value={branch}
                  onChange={setBranch}
                  options={visibleBranches}
                  label="Show data for"
                  className="mt-1.5"
                  selectClassName="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-900 focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                />
                <p className="mt-2 text-[10px] leading-snug text-slate-500">
                  Table, exports, and Receive / Add use this branch. Syncs with Outward Supply → Credit sale → <strong className="font-semibold text-slate-700">Outlet</strong>. Old rows
                  without branch → Main.
                </p>
              </div>

              <div>
                <p className="mb-2 text-[11px] font-bold uppercase text-slate-500">Actions</p>
                <div className="flex flex-col gap-2">
                  <button
                    type="button"
                    onClick={() => openReceive("")}
                    className="flex w-full items-center justify-center gap-2 rounded-lg bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700"
                  >
                    <Wallet className="h-4 w-4 shrink-0 opacity-90" aria-hidden />
                    Receive payment
                  </button>
                  <button
                    type="button"
                    onClick={openAdd}
                    className="flex w-full items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-800 hover:bg-slate-50"
                  >
                    <FilePlus2 className="h-4 w-4 shrink-0 text-slate-500" aria-hidden />
                    Add credit (manual)
                  </button>
                  <div className="grid grid-cols-2 gap-2">
                    <Link
                      to="/credit-due/payments"
                      className="flex items-center justify-center gap-1.5 rounded-lg border border-slate-300 bg-white px-2 py-2 text-center text-xs font-semibold text-slate-800 hover:bg-slate-50"
                    >
                      <History className="h-3.5 w-3.5 shrink-0 text-slate-500" aria-hidden />
                      History
                    </Link>
                    <button
                      type="button"
                      onClick={load}
                      className="flex items-center justify-center gap-1.5 rounded-lg border border-slate-300 bg-white px-2 py-2 text-xs font-semibold text-slate-800 hover:bg-slate-50"
                    >
                      <RefreshCw className="h-3.5 w-3.5 shrink-0 text-slate-500" aria-hidden />
                      Refresh
                    </button>
                  </div>
                </div>
              </div>

              <div>
                <p className="mb-2 flex items-center gap-1.5 text-[11px] font-bold uppercase text-slate-500">
                  <ListFilter className="h-3.5 w-3.5 text-slate-400" aria-hidden />
                  Filter table
                </p>
                <div className="space-y-2">
                  <div className="relative">
                    <Search
                      className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
                      aria-hidden
                    />
                    <input
                      type="search"
                      placeholder="Search name, phone, ID…"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="w-full rounded-lg border border-slate-300 bg-white py-2 pl-8 pr-2 text-sm placeholder:text-slate-400 focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                    />
                  </div>
                  <select
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                  >
                    <option value="all">All with open due</option>
                    <option value="overdue">Overdue only</option>
                    <option value="paid">Cleared bills (history)</option>
                  </select>
                  <label className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-700">
                    <input
                      type="checkbox"
                      checked={showCreditDays}
                      onChange={(e) => {
                        const v = e.target.checked;
                        setShowCreditDays(v);
                        try {
                          localStorage.setItem("creditDue_showCreditDays", v ? "1" : "0");
                        } catch {
                          // ignore
                        }
                      }}
                      className="rounded border-slate-400 text-indigo-600 focus:ring-indigo-500/30"
                    />
                    Show Credit days (Σ)
                  </label>
                </div>
              </div>

              <div>
                <p className="mb-2 flex items-center gap-1.5 text-[11px] font-bold uppercase text-slate-500">
                  <Table2 className="h-3.5 w-3.5 text-slate-400" aria-hidden />
                  Export
                </p>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => exportXlsx("due")}
                    className="flex items-center justify-center gap-1.5 rounded-lg border border-slate-300 bg-white px-2 py-2 text-xs font-semibold text-slate-800 hover:bg-slate-50"
                  >
                    <Table2 className="h-3.5 w-3.5 text-slate-500" aria-hidden />
                    Due XLSX
                  </button>
                  <button
                    type="button"
                    onClick={() => exportPdf("due")}
                    className="flex items-center justify-center gap-1.5 rounded-lg border border-slate-300 bg-white px-2 py-2 text-xs font-semibold text-slate-800 hover:bg-slate-50"
                  >
                    <FileDown className="h-3.5 w-3.5 text-slate-500" aria-hidden />
                    Due PDF
                  </button>
                  <button
                    type="button"
                    onClick={() => exportXlsx("aging")}
                    className="flex items-center justify-center gap-1.5 rounded-lg border border-slate-300 bg-white px-2 py-2 text-xs font-semibold text-slate-800 hover:bg-slate-50"
                  >
                    <Table2 className="h-3.5 w-3.5 text-slate-500" aria-hidden />
                    Aging XLSX
                  </button>
                  <button
                    type="button"
                    onClick={() => exportPdf("aging")}
                    className="flex items-center justify-center gap-1.5 rounded-lg border border-slate-300 bg-white px-2 py-2 text-xs font-semibold text-slate-800 hover:bg-slate-50"
                  >
                    <FileDown className="h-3.5 w-3.5 text-slate-500" aria-hidden />
                    Aging PDF
                  </button>
                </div>
              </div>

              <p className="text-[11px] leading-snug text-slate-500">
                <span className="font-semibold text-slate-700">Edit a bill:</span> open the customer, then{" "}
                <span className="font-semibold text-slate-800">Edit</span> on the row, or{" "}
                <span className="font-mono text-[10px] text-slate-600">/credit-due/bill/&lt;id&gt;/edit</span>.
              </p>
            </div>
          </aside>
        </div>
      </main>

      {receiveOpen && (
        <div
          className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/50 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="credit-receive-title"
        >
          <div
            className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
              <h2 id="credit-receive-title" className="text-base font-bold text-slate-900">
                Receive payment
              </h2>
              <button
                type="button"
                className="rounded-lg p-2 text-lg leading-none text-red-600 hover:bg-red-50"
                aria-label="Close"
                onClick={() => setReceiveOpen(false)}
              >
                ×
              </button>
            </div>
            <div className="p-4">
              <CreditReceivePaymentContent
                key={`${receiveKey}-${branch}`}
                branch={branch}
                initialCustomerId={receivePrefill}
                onSuccess={() => {
                  setReceiveOpen(false);
                  load();
                }}
              />
            </div>
          </div>
        </div>
      )}

      {addOpen && (
        <div
          className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/50 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="credit-add-title"
        >
          <div
            className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
              <h2 id="credit-add-title" className="text-base font-bold text-slate-900">
                Add manual credit (Udhar)
              </h2>
              <button
                type="button"
                className="rounded-lg p-2 text-lg leading-none text-red-600 hover:bg-red-50"
                aria-label="Close"
                onClick={() => setAddOpen(false)}
              >
                ×
              </button>
            </div>
            <div className="p-4">
              <CreditAddCreditContent
                key={`${addKey}-${branch}`}
                branch={branch}
                onSuccess={() => {
                  setAddOpen(false);
                  load();
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
